import React from "react";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { PackageType, getPackageConfig, formatPrice } from "../lib/package-config";
import { cn } from "../lib/utils";
import { FeatureTooltip } from "./feature-tooltip";

interface PackageDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  packageType: PackageType;
  price?: number;
  pricePerShow?: number;
  selectedShowCount?: number;
  onAddToProposal?: (packageType: PackageType, price: number) => void;
}

export function PackageDetailsModal({
  isOpen,
  onClose,
  packageType,
  price = 0,
  pricePerShow = 0,
  selectedShowCount = 0,
  onAddToProposal
}: PackageDetailsModalProps) {
  const config = getPackageConfig(packageType);
  const Icon = config.icon;

  const colorClasses = {
    primary: {
      'set-core': 'text-gray-600',
      'set-audio': 'text-blue-600',
      'set-video': 'text-purple-600',
      'custom': 'text-green-600'
    },
    background: {
      'set-core': 'bg-gray-50',
      'set-audio': 'bg-blue-50',
      'set-video': 'bg-purple-50',
      'custom': 'bg-green-50'
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <Icon className={cn("w-6 h-6", colorClasses.primary[packageType])} />
            <span className={colorClasses.primary[packageType]}>{config.name}</span>
            {config.badge && (
              <Badge className="bg-blue-600 text-white px-2 py-1 text-xs">
                {config.badge}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Package Overview */}
          <div className={cn("p-4 rounded-lg", colorClasses.background[packageType])}>
            <h3 className="font-semibold mb-2">Package Overview</h3>
            <p className="text-sm text-muted-foreground">{config.subtitle}</p>
            {price > 0 && (
              <div className="mt-3 flex items-center gap-4">
                <div>
                  <span className={cn("text-2xl font-bold", colorClasses.primary[packageType])}>
                    {formatPrice(price)}
                  </span>
                  <span className="text-sm text-muted-foreground ml-2">total</span>
                </div>
                {pricePerShow > 0 && (
                  <div>
                    <span className="text-lg font-semibold text-muted-foreground">
                      {formatPrice(pricePerShow)}
                    </span>
                    <span className="text-sm text-muted-foreground ml-1">per show</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Features */}
          <div>
            <h3 className="font-semibold mb-3">What's Included</h3>
            <div className="space-y-2">
              {config.features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className={cn(
                    "w-2 h-2 rounded-full mt-2 flex-shrink-0",
                    feature.highlight 
                      ? colorClasses.primary[packageType].replace('text-', 'bg-')
                      : "bg-gray-400"
                  )} />
                  <FeatureTooltip
                    text={feature.text}
                    title={feature.details?.title}
                    description={feature.details?.description}
                    imageUrl={feature.details?.imageUrl}
                    className={cn(
                      "text-sm",
                      feature.highlight && "font-medium"
                    )}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Additional Details Section - placeholder for now */}
          <div>
            <h3 className="font-semibold mb-3">Package Details</h3>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>• Professional brand integration throughout the event experience</p>
              <p>• Customizable activation opportunities based on your brand goals</p>
              <p>• Dedicated support throughout the planning and execution process</p>
              <p>• Post-event performance metrics and analytics</p>
            </div>
          </div>

          {/* Action Buttons */}
          {onAddToProposal && selectedShowCount > 0 && price > 0 && (
            <div className="flex gap-3 pt-4 border-t">
              <Button
                onClick={() => onAddToProposal(packageType, price)}
                className={cn(
                  "flex-1",
                  packageType === 'set-video' && "bg-purple-600 hover:bg-purple-700"
                )}
                variant={config.colorScheme.buttonVariant || 'default'}
              >
                Add {selectedShowCount} show{selectedShowCount !== 1 ? 's' : ''} to Plan
              </Button>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          )}
          
          {(!onAddToProposal || selectedShowCount === 0 || price === 0) && (
            <div className="flex justify-end pt-4 border-t">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}